import React, { useState } from 'react';
import { api } from '@/api/client';
import ConfirmDestructiveDialog from '@/components/ConfirmDestructiveDialog';
import { InlineToggle } from '@/components/ui';
import type { Job } from '@/api/types';
import { formatJobType, jobStatusLabel, shortId } from '@/utils/jobStatus';

type Props = {
  job: Job;
  onClose: () => void;
  onDone: (message: string) => void;
};

export default function ConfirmRemoveJobModal({ job, onClose, onDone }: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [alsoClearActivity, setAlsoClearActivity] = useState(false);
  const [error, setError] = useState('');

  async function confirm() {
    setSubmitting(true);
    setError('');
    try {
      await api.jobs.remove(job.id, { alsoClearActivity });
      onDone(
        alsoClearActivity
          ? `Job ${shortId(job.id)} and related activity removed`
          : `Job ${shortId(job.id)} removed`
      );
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Remove failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ConfirmDestructiveDialog
      title="Remove job"
      onClose={onClose}
      closeOnBackdrop={!submitting}
      footer={
        <>
          <button type="button" className="btn btn-ghost" disabled={submitting} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" disabled={submitting} onClick={() => void confirm()}>
            {submitting ? 'Removing…' : 'Remove job'}
          </button>
        </>
      }
    >
      {error && <div className="alert alert-error">{error}</div>}

      <p className="modal-lead">
        Remove durable job record <strong>{shortId(job.id)}</strong> ({formatJobType(job.type)},{' '}
        {jobStatusLabel(job.status)}) from operational views?
      </p>

      <InlineToggle
        checked={alsoClearActivity}
        onChange={setAlsoClearActivity}
        disabled={submitting}
        label="Also clear activity logs"
      />

      <p className="modal-hint muted">Removes deliveries, pipeline executions, and fetch runs linked to this job.</p>

      <div className="confirm-choice-card__audit">
        <p className="modal-hint">This hides from Jobs, Activity, and dashboard:</p>
        <ul>
          <li>The job record and its progress counters</li>
          <li>Job items, worker attempts, and dead-letter rows for this job</li>
        </ul>
        <p className="modal-hint">Audit rows are retained in the database but not shown in normal views.</p>
        <p className="modal-hint">This keeps:</p>
        <ul>
          <li>Discovered links (unless you also clear activity)</li>
          <li>Searches, subscriptions, and scrape audit data</li>
        </ul>
      </div>
    </ConfirmDestructiveDialog>
  );
}
